import AdminClient from '@/api/AdminClient';
import eagerFetchImage from '@/helpers/eager-fetch-image';

const baseSortByOptions = [
  { label: 'Full Name (Ascending)', value: 'FullNameAscPagingStrategy' },
  { label: 'Full Name (Descending)', value: 'FullNameDescPagingStrategy' },
  { label: 'Email (Ascending)', value: 'EmailAscPagingStrategy' },
  { label: 'Email (Descending)', value: 'EmailDescPagingStrategy' },
  { label: 'Date Joined (Ascending)', value: 'DateJoinedAscPagingStrategy' },
  { label: 'Date Joined (Descending)', value: 'DateJoinedDescPagingStrategy' },
  { label: 'Last Login (Ascending)', value: 'LastLoginAscPagingStrategy' },
  { label: 'Last Login (Descending)', value: 'LastLoginDescPagingStrategy' }
];

const typedSortByOptions = {
  individual: [
    { label: 'Credits Used (Ascending)', value: 'IndividualCreditsUsedAscPagingStrategy' },
    { label: 'Credits Used (Descending)', value: 'IndividualCreditsUsedDescPagingStrategy' }
  ],
  group: [
    { label: 'Credits Used (Ascending)', value: 'GroupCreditsUsedAscPagingStrategy' },
    { label: 'Credits Used (Descending)', value: 'GroupCreditsUsedDescPagingStrategy' }
  ]
};

export default class StudentDetailsReport {
  constructor() {
    this.adminClient = null;
    this.schoolId = 0;
    this.creditDisplayType = 'individual';
    this.sortBy = 'FullNameAscPagingStrategy'

    this.token = undefined;
    this.loadedOnce = false;

    this.parameterOptions = {
      sortBy: [
        ...baseSortByOptions,
        ...typedSortByOptions[this.creditDisplayType]
      ]
    };

    this.parameters = {
      pageToken: undefined,
      search: undefined,
      customCampusTags: undefined
    };

    this.data = {
      totalActiveStudents: undefined,
      totalInactiveStudents: undefined,
      studentDetails: []
    };
  }

  get loading() {
    return !this.token || this.token.loading;
  }

  get error() {
    return this.token && this.token.error;
  }

  get hasNext() {
    return !!this.parameters.pageToken;
  }

  async initialize($http, schoolId) {
    this.adminClient = new AdminClient($http);
    this.schoolId = schoolId;

    await this.fetchFirst();
  }

  async setCreditDisplayType(type) {
    if (this.creditDisplayType === type) {
      return;
    }

    const oldType = this.creditDisplayType;
    this.creditDisplayType = type;

    this.parameterOptions.sortBy = [
      ...baseSortByOptions,
      ...typedSortByOptions[type]
    ];

    // Swaps what we're sorting on based on the credit type change
    // Individual Asc <-> Workshop Asc
    // Individual Desc <-> Workshop Desc
    for(let i = 0; i < 2; i++) {
      if(typedSortByOptions[oldType][i].value === this.sortBy) {
        this.sortBy = typedSortByOptions[type][i].value;
        await this.fetchFirst();
        break;
      }
    }
  }

  async setParameters(parameters) {
    this.parameters = { ...this.parameters, ...parameters };

    if(parameters.pageToken)
      this.sortBy = parameters.pageToken;

    await this.fetchFirst();
  }

  async fetchFirst() {
    this.parameters.pageToken = this.sortBy;

    this.data = {
      ...this.data,
      studentDetails: []
    };

    await this.#fetch();
  }

  async fetchNext() {
    if (this.loading || !this.hasNext) {
      return;
    }

    await this.#fetch();
  }

  async #fetch() {
    if (this.token && this.token.loading) {
      this.token.canceled = true;
    }

    const token = {
      loading: true,
      error: undefined,
      canceled: false
    };
    this.token = token;

    try {
      const data = await this.adminClient.getCollegeAdminStudentDetailsReport(
        this.schoolId,
        this.parameters.pageToken,
        this.parameters.search,
        this.parameters.customCampusTags
      );

      if (token.canceled) {
        return;
      }

      const tasks = data.studentDetails.map(
        async (d) => (d.photoUrl = await eagerFetchImage(d.photoUrl))
      );
      await Promise.all(tasks);

      if (token.canceled) {
        return;
      }

      this.parameters.pageToken = data.nextPageToken;
      this.data = {
        ...data,
        totalActiveStudents:
          this.data.totalActiveStudents || data.activeStudents,
        totalInactiveStudents:
          this.data.totalInactiveStudents || data.inactiveStudents,
        studentDetails: [...this.data.studentDetails, ...data.studentDetails]
      };
    } catch (error) {
      token.error = error;
    } finally {
      token.loading = false;
      this.loadedOnce = true;
    }
  }
}
